import type { AxiosInstance } from "axios";
import type { z } from "zod";

import { parseCryptoAssetSymbol } from "~/v2/utils/cryptoAssets";
import { looseArray, looseParse } from "../looseParse";
import {
  cryptoAddressSchema,
  cryptoAssetSchema,
  type cryptoChainSchema,
  cryptoFeesSchema,
  cryptoTransferSchema,
  cryptoWhitelistedAddressSchema,
  type travelRuleInfoSchema,
  type travelRuleManualEntrySchema,
  type vaspSchema,
  vaspSearchResponseSchema,
} from "./cryptoWallet.schema";

export type CryptoChain = z.infer<typeof cryptoChainSchema>;

export type CryptoAsset = z.infer<typeof cryptoAssetSchema>;

export const listCryptoAssets = async (
  instance: AxiosInstance,
  accountId: string,
) => {
  const res = await instance.get<CryptoAsset[]>(
    `/accounts/${accountId}/assets`,
  );

  return looseParse(
    looseArray(cryptoAssetSchema, "listCryptoAssets"),
    res.data,
    "listCryptoAssets",
  );
};

export type CryptoAddress = z.infer<typeof cryptoAddressSchema>;

export const getCryptoAddress = async (
  instance: AxiosInstance,
  accountId: string,
  symbol: string,
) => {
  const { chain, asset } = parseCryptoAssetSymbol(symbol);
  const res = await instance.get<CryptoAddress>(
    `/accounts/${accountId}/addresses/${chain}/${asset}`,
  );
  return looseParse(cryptoAddressSchema, res.data, "getCryptoAddress");
};

export type CryptoTransfer = z.infer<typeof cryptoTransferSchema>;

export const getCryptoTransfers = async (
  instance: AxiosInstance,
  accountId: string,
) => {
  const res = await instance.get<CryptoTransfer[]>(
    `/accounts/${accountId}/transfers`,
  );
  return looseParse(
    looseArray(cryptoTransferSchema, "getCryptoTransfers"),
    res.data,
    "getCryptoTransfers",
  );
};

export interface CreateCryptoTransferPayload {
  amount: number;
  address: string;
  chain: string;
  asset: string;
  // Fee and waive fee are not used in old code but are in API... leaving logic the same as before - do not use for now.
  // fee?: number;
  // waive_fee?: boolean;
}

export const createCryptoTransfer = async (
  instance: AxiosInstance,
  accountId: string,
  payload: CreateCryptoTransferPayload,
) => {
  const res = await instance.post<CryptoTransfer>(
    `/accounts/${accountId}/transfers`,
    payload,
  );
  return looseParse(cryptoTransferSchema, res.data, "createCryptoTransfer");
};

export type TravelRuleManualEntry = z.infer<typeof travelRuleManualEntrySchema>;

export type TravelRuleInfo = z.infer<typeof travelRuleInfoSchema>;

export type Vasp = z.infer<typeof vaspSchema>;

export type VaspSearchResponse = z.infer<typeof vaspSearchResponseSchema>;

export type CryptoWhitelistedAddress = z.infer<
  typeof cryptoWhitelistedAddressSchema
>;

export const getWhitelistedAddresses = async (
  instance: AxiosInstance,
  accountId: string,
  isApproved?: boolean,
): Promise<CryptoWhitelistedAddress[]> => {
  const res = await instance.get<CryptoWhitelistedAddress[]>(
    `/accounts/${accountId}/whitelists`,
    {
      params: {
        is_approved: isApproved,
      },
    },
  );
  return looseParse(
    looseArray(cryptoWhitelistedAddressSchema, "getWhitelistedAddresses"),
    res.data,
    "getWhitelistedAddresses",
  );
};

export const getWhitelistedAddressesForSymbol = async (
  instance: AxiosInstance,
  accountId: string,
  symbol: string,
  isApproved?: boolean,
) => {
  const { chain, asset } = parseCryptoAssetSymbol(symbol);
  const res = await instance.get<CryptoWhitelistedAddress[]>(
    `/accounts/${accountId}/whitelists/${chain}/${asset}`,
    {
      params: {
        is_approved: isApproved,
      },
    },
  );
  return looseParse(
    looseArray(
      cryptoWhitelistedAddressSchema,
      "getWhitelistedAddressesForSymbol",
    ),
    res.data,
    "getWhitelistedAddressesForSymbol",
  );
};

interface CreateWhitelistPayloadBase {
  address: string;
  chain: string;
  asset: string;
  travel_rule_info?: TravelRuleInfo;
}

interface CreateWhitelistPayloadNoAddressType
  extends CreateWhitelistPayloadBase {
  address_type?: undefined;
  institution_name?: undefined;
}

interface CreateWhitelistPayloadPrivatelyHeld
  extends CreateWhitelistPayloadBase {
  address_type: "privately_held";
  institution_name?: undefined;
}

interface CreateWhitelistPayloadExchange extends CreateWhitelistPayloadBase {
  address_type: "exchange";
  institution_name: string;
}

export type CreateWhitelistedAddressPayload =
  | CreateWhitelistPayloadNoAddressType
  | CreateWhitelistPayloadPrivatelyHeld
  | CreateWhitelistPayloadExchange;

export const createWhitelistedAddress = async (
  instance: AxiosInstance,
  accountId: string,
  payload: CreateWhitelistedAddressPayload,
): Promise<CryptoWhitelistedAddress> => {
  const res = await instance.post<CryptoWhitelistedAddress>(
    `/accounts/${accountId}/whitelists`,
    payload,
  );
  return looseParse(
    cryptoWhitelistedAddressSchema,
    res.data,
    "createWhitelistedAddress",
  );
};

export const deleteWhitelistedAddress = async (
  instance: AxiosInstance,
  accountId: string,
  id: string,
) => {
  await instance.delete<void>(`/accounts/${accountId}/whitelists/${id}`);
};

export const updateTravelRuleInfo = async (
  instance: AxiosInstance,
  accountId: string,
  whitelistId: string,
  travelRuleInfo: TravelRuleInfo,
): Promise<CryptoWhitelistedAddress> => {
  const res = await instance.patch<CryptoWhitelistedAddress>(
    `/accounts/${accountId}/whitelists/${whitelistId}/travel-rule-info`,
    { travel_rule_info: travelRuleInfo },
  );
  return looseParse(
    cryptoWhitelistedAddressSchema,
    res.data,
    "updateTravelRuleInfo",
  );
};

export type CryptoFees = z.infer<typeof cryptoFeesSchema>;

export interface GetCryptoFeesParams {
  chain: string;
  asset?: string;
  from_address?: string;
  to_address?: string;
  amount?: number;
}

export const getCryptoFees = async (
  instance: AxiosInstance,
  accountId: string,
  params: GetCryptoFeesParams,
) => {
  const res = await instance.get<CryptoFees>(
    `/accounts/${accountId}/chains/fees`,
    { params },
  );
  return looseParse(cryptoFeesSchema, res.data, "getCryptoFees");
};

export interface SearchVaspsParams {
  query: string;
  page?: number;
  perPage?: number;
}

export const searchVasps = async (
  instance: AxiosInstance,
  accountId: string,
  { query, page = 0, perPage = 20 }: SearchVaspsParams,
) => {
  const res = await instance.get<VaspSearchResponse>(
    `/accounts/${accountId}/travel-rule/vasps`,
    { params: { q: query, page, per_page: perPage } },
  );
  return looseParse(vaspSearchResponseSchema, res.data, "searchVasps");
};

export type CreateWithdrawalArgs = Omit<
  CreateCryptoTransferPayload,
  "chain" | "asset"
> & { symbol: string };

interface CreateWhitelistArgsBase {
  chain: string;
  asset: string;
  address: string;
  travel_rule_info?: TravelRuleInfo;
}

interface CreateWhitelistArgsNoAddressType extends CreateWhitelistArgsBase {
  address_type?: undefined;
  institution_name?: undefined;
}

interface CreateWhitelistArgsPrivatelyHeld extends CreateWhitelistArgsBase {
  address_type: "privately_held";
  institution_name?: undefined;
}

interface CreateWhitelistArgsExchange extends CreateWhitelistArgsBase {
  address_type: "exchange";
  institution_name: string;
}

export type CreateWhitelistAddressArgs =
  | CreateWhitelistArgsNoAddressType
  | CreateWhitelistArgsPrivatelyHeld
  | CreateWhitelistArgsExchange;

export interface TravelRuleFieldOption {
  value: string;
  description: string;
}

export interface TravelRuleRequiredField {
  field: string;
  value_type: string;
  description: string;
  options?: TravelRuleFieldOption[];
}

export type TravelRuleNextActionType = "REQUIRED" | "ONE_OF";

export interface TravelRuleNextAction {
  name: string;
  description: string;
  type: TravelRuleNextActionType;
  required_fields: TravelRuleRequiredField[];
}

// Known wire error codes from `cryptowallet/external/notabene/models.go`.
// The `string & {}` arm preserves the union for known codes while still
// accepting any new code the backend introduces without a frontend release.
export type TravelRuleErrorCode =
  | "INVALID_DATA"
  | "UNKNOWN_VASP"
  | "ADDRESS_BELONGS_TO_VASP"
  | "TRAVEL_RULE_ACTION_REQUIRED"
  | "BENEFICIARY_NAME_REQUIRED"
  | (string & {});

export interface TravelRuleValidationError {
  error_code: TravelRuleErrorCode;
  message: string;
  next_actions: TravelRuleNextAction[];
}
